import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import useForm from '../../utils/useForm';
import { useQueryClient } from 'react-query';
import { useGlobalContext } from '../state/GlobalContext';
import { updateFrequency } from '../api';
import { formatFrequency } from '../utils';

interface FrequencyEditorForm {
  intervalCount: number;
  interval: string;
}

const FrequencyEditorStep2Modal: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { loadPreviousFlowStep, onClose } = useModalManagerContext();
  const { authToken, customerId, subscriptionId } = useGlobalContext();
  const queryClient = useQueryClient();

  const subscription = queryClient.getQueryData<Subscription>([
    'subscription',
    customerId,
    subscriptionId,
  ]);
  const [interval, intervalCount] = (
    subscription as Subscription
  ).BillingIntervalBillingCount.split('#');
  const { values, handleChange } = useForm<FrequencyEditorForm>({
    initialValues: {
      intervalCount: parseInt(intervalCount),
      interval,
    },
  });

  const handleIntervalCountChange = (valueAsString, valueAsNumber) => {
    handleChange({
      target: {
        name: 'intervalCount',
        value: !isNaN(valueAsNumber) ? valueAsNumber : '',
      },
    } as React.ChangeEvent<HTMLSelectElement>);
  };

  const handleSetFrequencyClick = async () => {
    setLoading(true);

    try {
      const res = await updateFrequency(customerId, subscriptionId, authToken, {
        intervalCount: values.intervalCount,
        interval: values.interval,
        isCustom: true,
      });

      updateQueryClientWithNewFrequency(res);
      showFrequencyUpdateSuccessNotification(res);
    } catch (err) {
      console.error(err);
      showFrequencyUpdateErrorNotification();
    } finally {
      setLoading(false);
    }
  };

  const showFrequencyUpdateSuccessNotification = (
    res: SubscriptionFrequency,
  ) => {
    onClose(
      t('subscription.frequency_update_success', {
        frequency: formatFrequency(
          t,
          res.DeliveryIntervalDeliveryCount,
          res.FormattedFrequency,
        ),
      }),
    );
  };

  const updateQueryClientWithNewFrequency = (res: SubscriptionFrequency) => {
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...res,
      }),
    );
  };

  const showFrequencyUpdateErrorNotification = () => {
    toast({
      isClosable: true,
      position: 'top',
      status: 'error',
      title: t('subscription.frequency_update_error'),
      variant: 'error',
    });
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.frequency.formModalTitle')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <Text>{t('cancelModal.frequency.formTitle')}</Text>
          <Flex mt="8">
            <FormControl id="intervalCount" mr="4">
              <NumberInput
                min={0}
                name="intervalCount"
                onChange={handleIntervalCountChange}
                value={values.intervalCount}
              >
                <NumberInputField
                  placeholder={t('cancelModal.frequency.formDelivery')}
                  required
                />
                <FormLabel>{t('cancelModal.frequency.formDelivery')}</FormLabel>
              </NumberInput>
            </FormControl>
            <FormControl id="securityCode">
              <Select
                name="interval"
                onChange={handleChange}
                required
                value={values.interval}
              >
                <option value="DAY">{t('subscription.frequency_days')}</option>
                <option value="WEEK">
                  {t('subscription.frequency_weeks')}
                </option>
                <option value="MONTH">
                  {t('subscription.frequency_months')}
                </option>
              </Select>
            </FormControl>
          </Flex>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={loadPreviousFlowStep} variant="outlinedBig">
          {t('cancelModal.back')}
        </Button>
        <Button
          disabled={
            !Number.isInteger(values.intervalCount) ||
            !values.interval ||
            loading
          }
          isLoading={loading}
          onClick={handleSetFrequencyClick}
          variant="blackBig"
        >
          {t('cancelModal.frequency.setFrequency')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default FrequencyEditorStep2Modal;
